import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

const WORKER_URL = process.env.REACT_APP_WORKER_URL;

// Converts a slug like "holy-sheet" to "Holy Sheet" for display
function formatDropName(slug) {
  if (!slug) return 'Your Drop';
  return slug
    .split('-')
    .map(function(w) { return w.charAt(0).toUpperCase() + w.slice(1); })
    .join(' ');
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
}

export default function Confirmation() {
  var params = useParams();
  var sessionId = params.sessionId;
  var navigate = useNavigate();

  // Stripe session data fetched from Worker GET /session/:sessionId
  var [session, setSession] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);

  // QR destination URL — buyer enters where recipients land when they scan the QR insert
  var [qrUrl, setQrUrl] = useState('');
  var [qrSaved, setQrSaved] = useState(false);   // true briefly after successful save
  var [qrSaving, setQrSaving] = useState(false); // true while POST is in flight

  // Redemption link reveal state
  var [copied, setCopied] = useState(false);
  var [unlocked, setUnlocked] = useState(false);
  var [unlocking, setUnlocking] = useState(false);
  var [redemptionLink, setRedemptionLink] = useState(null);

  // On mount: fetch Stripe session data from Worker
  // Returns drop slug, unit count, amount paid, and redemption link (if Make has written it yet)
  useEffect(function() {
    if (!sessionId) return;
    fetch(WORKER_URL + '/session/' + sessionId)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.error) throw new Error(data.error);
        setSession(data);
        setLoading(false);
      })
      .catch(function(err) {
        setError(err.message);
        setLoading(false);
      });
  }, [sessionId]);

  // Polls Worker for redemption link — Make writes it to Airtable after Stripe webhook fires.
  // Retries every 3 seconds until the link appears.
  function handleUnlock() {
    setUnlocking(true);
    fetch(WORKER_URL + '/session/' + sessionId)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.redemptionLink) {
          setRedemptionLink(data.redemptionLink);
          setUnlocked(true);
        } else {
          // Link not ready yet — retry in 3 seconds
          setTimeout(handleUnlock, 3000);
        }
        setUnlocking(false);
      })
      .catch(function() {
        setUnlocking(false);
      });
  }

  // Copies the redemption link to clipboard and shows a brief confirmation
  function handleCopy() {
    if (!redemptionLink) return;
    navigator.clipboard.writeText(redemptionLink);
    setCopied(true);
    setTimeout(function() { setCopied(false); }, 2000);
  }

  // POSTs the QR destination URL to Worker POST /drops/qr-url
  // Worker looks up the Drop record by slug and patches the QR Destination URL field
  // Silent fail to the user — errors logged to console only
  function handleSaveQr() {
    var url = qrUrl.trim();
    if (!url || !session?.dropSlug) return;

    setQrSaving(true);

    fetch(WORKER_URL + '/drops/qr-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dropSlug: session.dropSlug, qrUrl: url }),
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.error) {
          // Silent fail — log to console, don't surface to buyer
          console.error('QR URL save failed:', data.error);
        } else {
          // Show brief confirmation checkmark
          setQrSaved(true);
          setTimeout(function() { setQrSaved(false); }, 3000);
        }
        setQrSaving(false);
      })
      .catch(function(err) {
        // Network failure — silent fail
        console.error('QR URL save network error:', err);
        setQrSaving(false);
      });
  }

  // ── RENDER STATES ──────────────────────────────────────────

  if (loading) return <main><p>Loading your drop…</p></main>;

  if (error) return (
    <main>
      <p style={{ color: 'red' }}>Could not load drop details.</p>
      <button onClick={function() { navigate('/dashboard'); }}>Go to Dashboard</button>
    </main>
  );

  return (
    <main>
      <h1>{formatDropName(session.dropSlug)} is yours.</h1>
      <p>Share your redemption link whenever you're ready.</p>

      {/* Order summary pulled from Stripe session metadata */}
      <h2>Order Summary</h2>
      <p>Drop: {formatDropName(session.dropSlug)}</p>
      <p>Pieces: {session.unitCount}</p>
      <p>Amount paid: {formatCurrency(session.amountPaid)}</p>
      <p>Order ID: {sessionId}</p>

      {/* QR destination URL — where recipients land when they scan the QR insert in their box.
          Defaults to buyer's homepage if left blank. Saved to Airtable Drops table. */}
      <h2>QR Destination URL</h2>
      <p>Where should recipients land when they scan the QR insert? Defaults to your homepage if blank.</p>
      <input
        type="url"
        placeholder="https://yoursite.com/landing-page"
        value={qrUrl}
        onChange={function(e) { setQrUrl(e.target.value); }}
      />
      <button onClick={handleSaveQr} disabled={qrSaving}>
        {qrSaving ? 'Saving…' : 'Save'}
      </button>
      {qrSaved && <p>✓ Saved</p>}

      {/* Redemption link — shown masked until buyer clicks Unlock.
          Unlock polls the Worker until Make has written the link to Airtable. */}
      <h2>Your Redemption Link</h2>
      <p>Share this with your invitees. The clock starts on first redemption.</p>
      <p>
        {unlocked && redemptionLink ? redemptionLink : 'fmailers.com/r/••••••••/••••••••'}
        {unlocked
          ? <button onClick={handleCopy} style={{ marginLeft: '12px' }}>{copied ? '✓ Copied' : 'Copy'}</button>
          : <button onClick={handleUnlock} style={{ marginLeft: '12px' }} disabled={unlocking}>{unlocking ? 'Loading…' : 'Unlock'}</button>
        }
      </p>

      {/* Post-purchase instructions */}
      <h2>What Happens Next</h2>
      <ol>
        <li>Share your redemption link with your invitees via email, outbound, or direct message.</li>
        <li>First redemption opens the 48-hour window. Claims roll in on your dashboard.</li>
        <li>We pack and ship each piece after redemption — expect 1–3 weeks doorstep delivery.</li>
        <li>Each box includes a unique QR insert. Scans are attributed and visible in your dashboard.</li>
      </ol>

      <button onClick={function() { navigate('/dashboard'); }}>Go to Dashboard →</button>
    </main>
  );
}